import { motion } from "framer-motion";
import { NOXN } from "@/constants/testIds";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/7a9b6db3-da51-486d-b381-a72900b8fc1d/images/c371a76cb25fe848c920302f5418d709b49227e19714e164af48f76fb7e33779.png";

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 28 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = ({ onBegin }) => {
    return (
        <section
            id="top"
            data-testid={NOXN.heroSection}
            className="landing-hero-dark relative min-h-screen flex items-center overflow-hidden pt-28 pb-24 lg:pb-32"
        >
            {/* Background image + lighter-navy overlays (keeps original hero photo visible) */}
            <div className="absolute inset-0 -z-10">
                <img
                    src={HERO_BG}
                    alt="Futuristic digital billboard"
                    className="w-full h-full object-cover opacity-70"
                    loading="eager"
                />
                {/* Lighter navy tint — clearly blue, never black */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#1E3A6F]/55 via-[#2B4680]/70 to-[#1E3A6F]/85" />
                <div className="absolute inset-0 grid-noise opacity-30 mix-blend-overlay" />
                <div className="absolute inset-0 scanlines opacity-25 mix-blend-overlay" />
                {/* Ambient orange glow keeps the hero feeling alive */}
                <div className="pointer-events-none absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-45 blur-[130px]"
                    style={{ background: "radial-gradient(circle, rgba(255,90,0,0.55), rgba(255,90,0,0) 60%)" }} />
                <div className="pointer-events-none absolute bottom-0 -left-40 w-[500px] h-[500px] rounded-full opacity-30 blur-[110px]"
                    style={{ background: "radial-gradient(circle, rgba(59,90,160,0.55), transparent 60%)" }} />
            </div>

            <div className="mx-auto max-w-7xl px-6 lg:px-10 w-full">
                {/* Ticker / metadata strip */}
                <motion.div
                    {...fadeUp(0)}
                    className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-slate-400 flex items-center gap-3 mb-10"
                >
                    <span className="inline-block w-1.5 h-1.5 bg-[#FF5A00] animate-glow-pulse" />
                    NOXN / Out-of-home, redefined · v.2026
                </motion.div>

                <motion.h1
                    {...fadeUp(0.08)}
                    className="font-display font-black tracking-tighter text-white text-5xl sm:text-6xl lg:text-[6rem] leading-[0.92]"
                >
                    Own the <span className="text-[#FF5A00] text-glow-orange">Streets</span>.<br />
                    Own the <span className="text-[#FF5A00] text-glow-orange">Market</span>.
                </motion.h1>

                <motion.p
                    {...fadeUp(0.2)}
                    data-testid={NOXN.heroSubcopy}
                    className="mt-10 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
                >
                    Because waiting to be discovered is not a marketing strategy.
                </motion.p>

                <motion.div {...fadeUp(0.32)} className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <button
                        onClick={onBegin}
                        data-testid={NOXN.heroCtaPrimary}
                        className="group relative inline-flex items-center gap-3 bg-[#FF5A00] hover:bg-[#E65200] text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest transition-all duration-300 border-glow-orange"
                    >
                        Put Me On A Billboard
                        <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                    </button>

                    <a
                        href="#services"
                        data-testid={NOXN.heroCtaSecondary}
                        className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-white border border-white/15 hover:border-white/40 hover:bg-white/5 transition-all"
                    >
                        Explore ad space
                    </a>
                </motion.div>
            </div>
        </section>
    );
};
